/**
 * ZeroClassBanner.tsx
 *
 * PR 3 — Zero-class banner (BD-7 prerequisite).
 *
 * Owner Ruling 4 (law): when a performance prices as 0.0, the athlete MUST see
 * an explanation — never a bare unexplained zero. This banner fires when
 * `metadata.pricing_result.is_zero_class === true` and names each dimension
 * that triggered the zero in plain coaching language.
 *
 * Stacking decision (automatic_fail + zero-class):
 * When BOTH `automatic_fail` and `is_zero_class` are true, render ONLY the
 * automatic_fail banner (the gate-failure banner is already the most specific
 * explanation, listing each failed goal by name). Rendering both would confuse
 * the athlete with duplicate "score = 0" messaging. The `suppressIfAutoFail`
 * prop implements this; EvaluationResults.tsx passes `evaluation.automatic_fail`
 * as its value.
 *
 * Plain-language copy: uses `dimensionLabel` from diagnosticTranslations for
 * human-readable dimension names. Coaching copy never exposes z-scores, Kc/Ks
 * boundaries, or any statistical measure. Numbers live in DebugDiagnosticsPanel.
 *
 * Fallback sentence: if `overallScore === 0` and there is no coaching narrative
 * summary (no `llm_explanation.overall_summary`), the banner appends a
 * deterministic fallback sentence so the athlete is never left with only a "0"
 * and no context.
 *
 * All-users: no role gate. Owner Ruling 4 applies most urgently to athletes.
 */

import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { coachFacingDimensionLabel } from '@/utils/diagnosticTranslations';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface ZeroClassBannerProps {
  /**
   * The full pricing_result from evaluation.metadata.pricing_result.
   * When absent (legacy evals, feature flag off): banner does not render.
   */
  pricingResult?: {
    is_zero_class: boolean;
    /** EXP-010 A7: present only when the pricing ceiling flag is on. */
    zero_class_ceiling?: { enabled: boolean; applied: boolean; cap: number | null; r: number | null; dimension: string | null; effective_score?: number | null };
    dims_fired: string[];
  } | null;

  /**
   * Pass `true` when `evaluation.automatic_fail` is true.
   * In that case the automatic_fail banner already explains the zero; this
   * banner is suppressed to avoid duplicate messaging.
   */
  suppressIfAutoFail?: boolean;

  /**
   * The evaluation's overall_score (raw value, before normalizeScore).
   * Used to gate the fallback sentence: only appended when score is truly 0.
   */
  overallScore?: number | null;

  /**
   * Whether a coaching narrative summary is present.
   * Pass `true` when `evaluation.llm_explanation?.overall_summary` is a
   * non-empty string. When false and score is 0, the fallback sentence renders.
   */
  hasCoachingNarrative?: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Sentence-case the first character, leaving the rest alone. */
function sentenceCase(text: string): string {
  return text.length === 0 ? text : text.charAt(0).toUpperCase() + text.slice(1);
}

/**
 * Build the plain-language sentence listing what went far outside the usual
 * range, using the coach-facing labels (NOT the engineering ones — the old
 * copy read "Motion Orchestration and Body Orientation", which is the
 * internal name of a scoring dimension, not something a coach says).
 *
 * Examples:
 *   ["B_motion"]                                 → "Timing"
 *   ["B_motion","A_orientation"]                 → "Timing and body position"
 *   ["B_motion","A_orientation","F_foot_dir"]     → "Timing, body position, and foot direction"
 */
function buildDimList(dimsFired: string[]): string {
  const labels = dimsFired.map(coachFacingDimensionLabel);
  if (labels.length === 0) return 'part of your movement';
  if (labels.length === 1) return sentenceCase(labels[0]);
  if (labels.length === 2) return `${sentenceCase(labels[0])} and ${labels[1]}`;
  const all = [...labels];
  const last = all.pop();
  return `${sentenceCase(all.join(', '))}, and ${last}`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * ZeroClassBanner renders when pricing marks this evaluation as 0-class.
 * It names the dimensions that fired in plain coaching language.
 */
export default function ZeroClassBanner({
  pricingResult,
  suppressIfAutoFail = false,
  overallScore,
  hasCoachingNarrative = true,
}: ZeroClassBannerProps) {
  // No banner when pricing data absent (legacy evals / feature flag off)
  if (!pricingResult) return null;

  // Not a zero-class verdict — banner does not apply
  if (!pricingResult.is_zero_class) return null;
  // EXP-010 A7: when the pricing ceiling replaced the hard zero (flag-gated on
  // the backend), say the capped number instead of "0" — the explanation and
  // the "what to do" sentence are unchanged (owner decision P-2).
  const ceiling = pricingResult.zero_class_ceiling;
  // Name the score the athlete actually sees: the effective score when recorded
  // (it can sit below the cap when another ceiling held it lower), else the cap.
  const cappedScore: number | null =
    ceiling && ceiling.applied
      ? typeof ceiling.effective_score === 'number'
        ? Math.round(ceiling.effective_score)
        : typeof ceiling.cap === 'number'
          ? Math.round(ceiling.cap)
          : null
      : null;

  // Suppress when automatic_fail already explains the zero (avoid double messaging)
  if (suppressIfAutoFail) return null;

  const dimsFired = pricingResult.dims_fired ?? [];
  const dimList = buildDimList(dimsFired);
  // The list is the sentence's subject, so the verb has to agree with it.
  const dimVerb = dimsFired.length > 1 ? 'were' : 'was';

  // Score is 0 when overall_score is 0 or 0.0 (raw value before normalizeScore)
  const scoreIsZero =
    overallScore === 0 || overallScore === 0.0 || overallScore === null || overallScore === undefined;

  // Fallback sentence: only when score is genuinely 0 AND coaching narrative absent
  const showFallback = scoreIsZero && !hasCoachingNarrative;

  return (
    <div
      className="bg-red-900/90 border-2 border-red-500 rounded-lg p-5 shadow-lg"
      data-testid="zero-class-banner"
      role="alert"
      aria-live="polite"
    >
      <div className="flex items-start gap-3">
        <ExclamationTriangleIcon
          className="h-7 w-7 text-red-400 flex-shrink-0 mt-0.5"
          aria-hidden="true"
        />
        <div className="flex-1">
          {/* WHAT / WHY / HOW — docs/COACH_FACING_COPY_STANDARD.md. The old
              copy ("Movement Outside Acceptable Range" … "deviated far
              outside the acceptable range for Motion Orchestration and Body
              Orientation") named an internal scoring dimension and gave the
              athlete nothing to do. */}
          <h3 className="text-lg font-bold text-red-100">
            {cappedScore !== null
              ? `Score held to ${cappedScore} — this attempt was too far off to grade fully`
              : 'Score: 0 — this attempt was too far off to grade'}
          </h3>

          <p className="text-red-200 text-sm mt-1 mb-3">
            <span
              className="font-semibold text-red-100"
              data-testid="zero-class-dim-list"
            >
              {dimList}
            </span>{' '}
            {dimVerb} a long way from your coach's demo, so we didn't give this attempt a
            grade rather than score a movement we can't compare fairly. Watch the comparison
            above to see where it went off, then film the movement again.
          </p>

          {showFallback && (
            <p
              className="text-red-300 text-sm mt-2 border-t border-red-700/50 pt-3"
              data-testid="zero-class-fallback-sentence"
            >
              We don't have written coaching for this attempt yet. Watch the
              side-by-side comparison above to see where your movement differs
              from your coach's demo, and ask your coach what to work on first.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
